/**
 * Transport layer for talking to `apps/server`. The typed endpoints live in `endpoints.ts`;
 * this file owns the mechanics every request shares — base URL, auth header, error shape.
 */

/**
 * Whether the server refused with a particular reason of its own. A refusal carries a code in
 * its body — `out-of-room`, `plan-has-no-ai` — and what the screen says depends on which, so
 * the reading of it belongs in one place rather than beside every message.
 */
export function refusedBecause(error: unknown, said: string): boolean {
  return error instanceof ApiError && (error.body as { error?: string } | null)?.error === said
}

export class ApiError extends Error {
  readonly status: number
  readonly url: string
  /** What the server said with the status, where it said anything that parsed as JSON. */
  readonly body: unknown

  constructor(status: number, url: string, message: string, body: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.url = url
    this.body = body
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string
  /** Injectable so tests can drive the client without a network. */
  readonly fetch?: typeof globalThis.fetch
  /** Resolves the session token attached to every request; null when signed out. */
  readonly getToken?: () => string | null
  /** Called on a 401 — a dead session must not look like a broken app. */
  readonly onUnauthorized?: () => void
}

export interface ApiClient {
  request: <T>(path: string, init?: RequestInit) => Promise<T>
  /** A file rather than an answer: the bytes as they came, for handing to the browser to save. */
  download: (path: string) => Promise<Blob>
}

/**
 * A request stopped on purpose, told apart from one that failed. A cancel is not a dropped
 * connection: the queue must carry on to the next thing rather than assuming it is offline
 * and giving up on the whole pass.
 */
export class Aborted extends Error {
  constructor(url: string) {
    super(`Request to ${url} was stopped`)
    this.name = 'Aborted'
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl } = options
  const doFetch =
    options.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args))

  async function send(path: string, accept: string, init?: RequestInit): Promise<Response> {
    if (!path.startsWith('/')) {
      throw new Error(`API path must start with "/", got ${JSON.stringify(path)}`)
    }

    const url = `${baseUrl}${path}`
    const headers = new Headers(init?.headers)
    if (!headers.has('Accept')) {
      headers.set('Accept', accept)
    }

    const token = options.getToken?.() ?? null
    if (token !== null && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const response = await doFetch(url, { ...init, headers })

    if (!response.ok) {
      if (response.status === 401) {
        options.onUnauthorized?.()
      }
      throw new ApiError(
        response.status,
        url,
        `Request to ${path} failed with status ${response.status}`,
        await response.json().catch(() => null),
      )
    }
    return response
  }

  return {
    async download(path: string): Promise<Blob> {
      return await (await send(path, '*/*')).blob()
    },
    async request<T>(path: string, init?: RequestInit): Promise<T> {
      const response = await send(path, 'application/json', init)

      if (response.status === 204) {
        return undefined as T
      }

      return (await response.json()) as T
    },
  }
}
